/**
 * The backdrop behind the sign-in screen, named by the brand profile
 * (`identity.signInBackdrop`).
 *
 * **Ported, not designed here.** `sweetertrade` is the hero of
 * sweetertrade.com (its `Hero.tsx`), layer for layer: the base gradient, the gold
 * sunrise, the sapphire crown, the drifting blue grid, the gold floor and the
 * light rays — same colours, sizes and periods. Its floating particles are left
 * out: 200 lines of script animation for a screen somebody spends ten seconds on.
 *
 * A component rather than an image because the look *is* the layering and the
 * slow pulses; a flattened picture would be the one thing it is not. The
 * keyframes live in a `<style>` here because this app styles with objects and
 * has no stylesheet a keyframe could go in. All motion stops for somebody who
 * asked their system for less.
 *
 * `qufolio` is qufolio.com's hero sky (`lib/star-sky.ts`): one of its ten
 * skies, a different one from last visit, drawn on a canvas.
 *
 * Purely decorative: `aria-hidden`, no pointer events, under everything.
 */

import { useEffect, useRef } from "react";
import type { ResolvedIdentity } from "../lib/brand";
import { pickSky, SKIES, startSky } from "../lib/star-sky";

type Backdrop = NonNullable<ResolvedIdentity["signInBackdrop"]>;

export function SignInBackdrop({ name }: { name: Backdrop }) {
  if (name === "qufolio") return <StarSky />;
  return (
    <div className="qf-bd" aria-hidden="true">
      <style>{SWEETERTRADE_CSS}</style>
      <div className="qf-bd-base" />
      <div className="qf-bd-gold" />
      <div className="qf-bd-sapphire" />
      <div className="qf-bd-grid" />
      <div className="qf-bd-floor qf-bd-floor-h" />
      <div className="qf-bd-floor qf-bd-floor-v" />
      {RAYS.map((ray, i) => (
        <div
          key={i}
          className={`qf-bd-ray ${ray.narrow ? "qf-bd-ray-narrow" : "qf-bd-ray-wide"}`}
          style={{
            left: ray.left,
            transform: `rotate(${ray.rot})`,
            background: `linear-gradient(to bottom, transparent 0%, ${ray.color} 15%, transparent 85%)`,
            animationDelay: `${(ray.narrow ? i - 5 : i) * 0.5}s`,
          }}
        />
      ))}
    </div>
  );
}

/** qufolio.com's sky, on a canvas the size of the screen. */
function StarSky() {
  const canvas = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    if (!canvas.current) return;
    return startSky(canvas.current, SKIES[pickSky()]!);
  }, []);
  return (
    <div className="qf-bd" aria-hidden="true" style={SKY_ROOT}>
      <canvas ref={canvas} style={SKY_CANVAS} />
    </div>
  );
}

const SKY_ROOT: React.CSSProperties = { position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none", zIndex: 0, background: "#03040a" };
const SKY_CANVAS: React.CSSProperties = { display: "block", width: "100%", height: "100%" };

const GOLD = (a: number) => `rgba(230,165,20,${a})`;
const BLUE = (a: number) => `rgba(74,158,255,${a})`;

/** The site's two ray sets: five wide, three under 768px. */
const RAYS = [
  { left: "18%", rot: "22deg", color: GOLD(0.85), narrow: false },
  { left: "34%", rot: "8deg", color: BLUE(0.8), narrow: false },
  { left: "50%", rot: "0deg", color: GOLD(0.75), narrow: false },
  { left: "66%", rot: "-9deg", color: BLUE(0.75), narrow: false },
  { left: "82%", rot: "-24deg", color: GOLD(0.8), narrow: false },
  { left: "10%", rot: "38deg", color: GOLD(0.85), narrow: true },
  { left: "50%", rot: "0deg", color: GOLD(0.75), narrow: true },
  { left: "90%", rot: "-38deg", color: GOLD(0.8), narrow: true },
];

const FLOOR_MASK = "radial-gradient(ellipse 100% 100% at 50% 100%, black 0%, transparent 72%)";
const GRID_MASK = "radial-gradient(ellipse 92% 88% at 50% 50%, black 0%, transparent 84%)";

const SWEETERTRADE_CSS = `
.qf-bd { position: absolute; inset: 0; overflow: hidden; pointer-events: none; z-index: 0; }
.qf-bd > div { position: absolute; }
.qf-bd-base { inset: 0; background: linear-gradient(to bottom right, #080612, #050410, #060308); }
.qf-bd-gold {
  bottom: -5%; left: 50%; width: 160%; height: 70%; filter: blur(18px);
  transform: translateX(-50%);
  background: radial-gradient(ellipse 80% 70% at 50% 100%, ${GOLD(0.85)} 0%, rgba(210,145,10,0.55) 20%,
    rgba(190,130,5,0.28) 42%, rgba(160,100,0,0.10) 62%, transparent 76%);
  animation: qf-bd-gold 6s ease-in-out infinite;
}
.qf-bd-sapphire {
  top: -5%; left: 50%; width: 140%; height: 72%; filter: blur(22px);
  transform: translateX(-50%);
  background: radial-gradient(ellipse 70% 60% at 50% 0%, ${BLUE(0.65)} 0%, rgba(60,130,240,0.38) 25%,
    rgba(43,100,220,0.16) 50%, rgba(30,70,180,0.05) 68%, transparent 78%);
  animation: qf-bd-sapphire 8s ease-in-out infinite 0.8s;
}
.qf-bd-grid {
  inset: 0; opacity: 0.2;
  background-image: linear-gradient(${BLUE(0.22)} 1px, transparent 1px),
    linear-gradient(90deg, ${BLUE(0.22)} 1px, transparent 1px);
  background-size: 56px 56px;
  -webkit-mask-image: ${GRID_MASK}; mask-image: ${GRID_MASK};
  animation: qf-bd-grid 16s linear infinite;
}
.qf-bd-floor {
  bottom: 0; left: 50%; width: 200%; height: 58%; transform: translateX(-50%);
  -webkit-mask-image: ${FLOOR_MASK}; mask-image: ${FLOOR_MASK};
}
.qf-bd-floor-h {
  background: repeating-linear-gradient(to top, transparent, transparent 43px, ${GOLD(0.38)} 43px, ${GOLD(0.38)} 44px);
}
.qf-bd-floor-v {
  background: repeating-linear-gradient(to right, transparent, transparent 63px, ${GOLD(0.3)} 63px, ${GOLD(0.3)} 64px);
}
.qf-bd-ray { top: -5%; width: 5px; height: 120%; filter: blur(3px); animation: qf-bd-ray 10s ease-in-out infinite; }
.qf-bd-ray-narrow { display: none; }
@media (max-width: 767px) {
  .qf-bd-ray-wide { display: none; }
  .qf-bd-ray-narrow { display: block; }
}
@keyframes qf-bd-gold {
  0%, 100% { opacity: 1; transform: translateX(-50%) scaleX(1); }
  50% { opacity: 0.85; transform: translateX(-50%) scaleX(1.04); }
}
@keyframes qf-bd-sapphire {
  0%, 100% { opacity: 1; transform: translateX(-50%) scaleX(1); }
  50% { opacity: 0.82; transform: translateX(-50%) scaleX(1.04); }
}
@keyframes qf-bd-grid { 0% { background-position: 0 0; } 100% { background-position: 56px 56px; } }
@keyframes qf-bd-ray { 0%, 100% { opacity: 0.9; } 50% { opacity: 0.4; } }
@media (prefers-reduced-motion: reduce) {
  .qf-bd > div { animation: none !important; }
}
`;
